import React, { useState, useEffect, useContext, useRef } from 'react'
import { Text, View } from 'react-native'
import { textStyles } from '../style'
import HorizontalList from '../horizontal-list'
import { Company, Dictionary } from '@ecountabl/lib'
import { log } from '../utility'
import api from '../api-client'
import { useAppSelector } from '../store'
import ThemedDialog from '../style/dialog'
import { ThemeContext } from '../utility/context/ThemeContext'
import { Ionicons } from '@expo/vector-icons'
import { CompanyProfileRouteParams } from '.'
import AlternativeStatus from './alternative-status'

type Props = {
  company: Company
  showAlternatives?: boolean
}

const companyIdOf = (company: Company): string => {
  const withId = company as Company & { id?: string }
  return String(company._id || withId.id || '')
}

export const Alternatives = ({ company, showAlternatives }: Props): React.ReactElement | null => {
  const id = companyIdOf(company)

  const { user } = useAppSelector(s => s.auth)

  const theme = useContext(ThemeContext)
  const { textMain } = theme.theme.colors

  const [altsByTag, setAltsByTag] = useState<Dictionary<Company[]>>({})
  const [loadingAlts, setLoadingAlts] = useState(showAlternatives !== false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [showMore, setShowMore] = useState(user._id ? true : false)
  const [errorState, setErrorState] = useState(false)
  const requestCompanyId = useRef(id)
  requestCompanyId.current = id

  useEffect(() => {
    if (showAlternatives === false || !id) {
      setLoadingAlts(false)
      setAltsByTag({})
      return
    }

    let cancelled = false
    setLoadingAlts(true)
    setAltsByTag({})
    setShowMore(user._id ? true : false)

    api.getCompanyAlternatives(id).then(res => {
      if (cancelled || requestCompanyId.current !== id) return
      setAltsByTag(res.altsByTag || {})
    }).catch(ex => {
      if (cancelled || requestCompanyId.current !== id) return
      log.error(ex, { tags: { message: 'error fetching company alternatives' } })
      setErrorState(true)
    }).finally(() => {
      if (!cancelled && requestCompanyId.current === id) setLoadingAlts(false)
    })

    return () => {
      cancelled = true
    }
  }, [id, showAlternatives, user._id])

  const onLoadMore = () => {
    if (!id) return
    const requestedId = id
    setLoadingMore(true)
    api.getCompanyAlternatives(id, { maxAlts: 300 }).then(res => {
      if (requestCompanyId.current !== requestedId) return
      setAltsByTag(res.altsByTag || {})
      setShowMore(false)
    }).catch(ex => {
      if (requestCompanyId.current !== requestedId) return
      log.error(ex, { tags: { message: 'error loading more companies' } })
      setErrorState(true)
    }).finally(() => {
      if (requestCompanyId.current === requestedId) setLoadingMore(false)
    })
  }

  if (showAlternatives === false) return null

  const addlProfileParams: Partial<CompanyProfileRouteParams> = {
    source: 'alt',
    sourceCompanyId: company._id,
    sourceCompanyName: company.name
  }
  const hasAlts = Object.keys(altsByTag).length > 0

  return <>
    {loadingAlts && !hasAlts ? (
      <View style={{ width: '100%', justifyContent: 'center', alignItems: 'center', padding: 10 }}>
        <AlternativeStatus style={[textStyles.body, { color: textMain }]} />
      </View>
    ) : null}
    {hasAlts ? <View style={{gap: 10}}>
      {
        Object.entries(altsByTag).map(([key, alts]) => (
          <View key={key}>
            <Text style={[textStyles.h3, {marginBottom: 8, color: textMain}]}>{`Alternatives in ${key}`}</Text>
            <HorizontalList
              id={`${key}-alts`}
              companies={alts}
              loadingMore={loadingMore}
              onLoadMore={onLoadMore}
              showMore={showMore}
              addlProfileParams={{...addlProfileParams, tag: key}}
            />
          </View>
        ))
      }
      </View>
      : null
    }
    <ThemedDialog
        visible={errorState}
        title="Error"
        subMessage="An error occurred while retrieving company information. Please try again later."
        errorAnimation={errorState}
        options={[
          {
            title: "Okay",
            role: "destructive",
            icon: <Ionicons name="checkmark" size={22} />,
            onPress: () => setErrorState(false),
          },
        ]}
      />
  </>
}

export default Alternatives
